/**
 * useWlTenantSearch
 * ----------------------------------------------------------------------------
 * Hook de busca server-side de tenants para o filtro `WlTenantFilter`.
 * Reutiliza a RPC `wl_list_tenants` (que já filtra por nome, slug, email do
 * owner, telefone do owner e, a partir da v1.2.1, full_name do owner).
 *
 * - Sempre limita a 10 resultados (`p_limit=10`).
 * - Sem termo de busca → retorna os 10 mais recentes (ordenação da RPC).
 * - Não sofre cap de 1000 do PostgREST: tudo passa pela RPC paginada.
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface WlTenantSearchItem {
  id: string;
  name: string;
  slug: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone_masked: string | null;
}

/** Busca server-side (top 10) de tenants para popular filtros. */
export function useWlTenantSearch(search: string, enabled = true) {
  const term = search.trim();

  return useQuery({
    queryKey: ["wl-tenant-search", term],
    queryFn: async (): Promise<WlTenantSearchItem[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenants", {
        p_search: term.length > 0 ? term : null,
        p_status: null,
        p_plan: null,
        p_limit: 10,
        p_offset: 0,
        p_created_from: null,
        p_created_to: null,
      });
      if (error) {
        logger.error("[useWlTenantSearch]", error.message);
        throw error;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((t) => ({
        id: t.id,
        name: t.name,
        slug: t.slug ?? null,
        owner_name: t.owner_name ?? null,
        owner_email: t.owner_email ?? null,
        owner_phone_masked: t.owner_phone_masked ?? null,
      }));
    },
    enabled,
    staleTime: 30_000,
  });
}
